import { PanelLeftClose, PanelLeft, LogOut, X } from 'lucide-react'
import { cn }              from '@/lib/utils'
import { useUIStore }      from '@/stores/ui.store'
import { useAuthStore }    from '@/stores/auth.store'
import { supabase }        from '@/lib/supabase'
import { NavItem }         from './NavItem'
import { getVisibleSections } from '@/lib/navigation'
import { LOGO_PROCREDITO } from '@/assets/logo'

async function handleSignOut() {
  useAuthStore.getState().reset()
  await supabase.auth.signOut().catch(() => {})
  window.location.replace('/login')
}

function RoleBadge({ role }: { role: string }) {
  const cfg: Record<string, { bg: string; text: string }> = {
    admin:      { bg: 'rgba(45,94,255,0.12)',  text: '#5582ff' },
    supervisor: { bg: 'rgba(217,119,6,0.12)',  text: '#f59e0b' },
    agente:     { bg: 'rgba(22,163,74,0.12)',  text: '#22c55e' },
  }
  const c = cfg[role] ?? { bg: 'rgba(107,114,128,0.12)', text: '#9ca3af' }
  const labels: Record<string, string> = { admin:'Admin', supervisor:'Supervisor', agente:'Agente' }
  return (
    <span className="inline-flex items-center rounded-md px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest"
      style={{ background: c.bg, color: c.text }}>
      {labels[role] ?? role}
    </span>
  )
}

function NavContent({ collapsed = false }: { collapsed?: boolean }) {
  const role    = useAuthStore(s => s.role)
  const loading = useAuthStore(s => s.loading)
  if (loading) return (
    <nav className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-4 space-y-2">
      {[1,2,3,4,5].map(i => (
        <div key={i} className="h-8 rounded-lg animate-pulse bg-[var(--nav-hover-bg)]" style={{ opacity: 0.4, width:`${55+i*8}%` }} />
      ))}
    </nav>
  )
  const sections = getVisibleSections(role)
  return (
    <nav className="flex-1 overflow-y-auto overflow-x-hidden px-2 py-3 space-y-5 scrollbar-hide">
      {sections.map(section => (
        <div key={section.id}>
          {!collapsed
            ? (
              <div className="flex items-center gap-2 mb-1.5 px-2">
                <p className="text-[9px] font-bold uppercase tracking-[0.14em] text-[var(--text-muted)] select-none">
                  {section.label}
                </p>
                <div className="flex-1 h-px bg-[var(--border)]" />
              </div>
            )
            : <div className="mx-auto mb-2 h-px w-5 bg-[var(--border)] opacity-40" />
          }
          <div className="space-y-0.5">
            {section.modules.map(m => <NavItem key={m.id} module={m} collapsed={collapsed} />)}
          </div>
        </div>
      ))}
    </nav>
  )
}

export function Sidebar() {
  const collapsed     = useUIStore(s => s.sidebarCollapsed)
  const toggleSidebar = useUIStore(s => s.toggleSidebar)
  const profile       = useAuthStore(s => s.profile)
  const authLoading   = useAuthStore(s => s.loading)
  const initial       = authLoading ? '·' : (profile?.full_name?.[0] ?? profile?.username?.[0] ?? 'U').toUpperCase()

  return (
    <aside className={cn(
      'hidden md:flex fixed left-0 top-0 z-30 h-screen flex-col',
      'border-r border-[var(--border)] bg-[var(--sidebar-bg)]',
      'transition-[width] duration-200 ease-out',
      collapsed ? 'w-[56px]' : 'w-[230px]',
    )}>
      {/* Top accent */}
      <div className="absolute top-0 left-0 right-0 h-px opacity-50"
        style={{ background: 'linear-gradient(90deg, transparent, #2d5eff, transparent)' }} />

      {/* Logo header */}
      <div className={cn(
        'flex h-[56px] shrink-0 items-center border-b border-[var(--border)]',
        collapsed ? 'justify-center px-0' : 'gap-3 px-4'
      )}>
        <div className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border',
          'bg-[var(--bg-surface-2)] border-[var(--border)]',
        )}>
          <img src={LOGO_PROCREDITO} alt="ProCredito" className="h-6 w-6 object-contain" />
        </div>
        {!collapsed && (
          <>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px] font-bold text-[var(--text-primary)] leading-none tracking-tight">
                PROCREDITO
              </p>
              <p className="truncate text-[9px] text-[var(--text-muted)] mt-0.5 leading-none tracking-[0.15em] uppercase">
                Dominicana
              </p>
            </div>
            <div className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse shrink-0" title="Sistema en línea" />
          </>
        )}
      </div>

      <NavContent collapsed={collapsed} />

      {/* Footer */}
      <div className="shrink-0 border-t border-[var(--border)] p-2 space-y-1">
        {!collapsed && (profile || authLoading) && (
          <div className="rounded-lg border border-[var(--border)] bg-[var(--bg-surface-2)]/50 p-2.5">
            <div className="flex items-center gap-2.5">
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-brand-500/15 text-brand-400 font-bold text-xs border border-brand-500/20">
                {initial}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[11px] font-semibold text-[var(--text-primary)] leading-none">
                  {profile.full_name ?? profile.username}
                </p>
                <div className="mt-1"><RoleBadge role={profile.role} /></div>
              </div>
              <button onClick={handleSignOut}
                className="shrink-0 p-1 rounded text-[var(--text-muted)] hover:text-red-400 hover:bg-red-400/10 transition-colors"
                title="Cerrar sesión">
                <LogOut size={13} strokeWidth={1.8} />
              </button>
            </div>
          </div>
        )}

        <button onClick={toggleSidebar}
          className={cn(
            'flex w-full items-center gap-2 rounded-lg px-2.5 py-2',
            'text-[11px] text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--nav-hover-bg)]',
            'transition-all duration-150',
            collapsed && 'justify-center'
          )}
          title={collapsed ? 'Expandir' : 'Colapsar'}>
          {collapsed
            ? <PanelLeft size={14} strokeWidth={1.8} />
            : <><PanelLeftClose size={14} strokeWidth={1.8} /><span>Colapsar</span></>
          }
        </button>
      </div>
    </aside>
  )
}

export function MobileSidebar() {
  const mobileOpen = useUIStore(s => s.sidebarMobileOpen)
  const closeMenu  = useUIStore(s => s.closeMobileSidebar)
  const profile    = useAuthStore(s => s.profile)
  const initial    = (profile?.full_name?.[0] ?? profile?.username?.[0] ?? 'U').toUpperCase()

  return (
    <>
      <div className={cn(
        'fixed inset-0 z-40 bg-black/70 backdrop-blur-sm md:hidden transition-opacity duration-200',
        mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
      )} onClick={closeMenu} aria-hidden="true" />

      <aside className={cn(
        'fixed left-0 top-0 z-50 flex h-full w-[270px] flex-col md:hidden',
        'border-r border-[var(--border)] bg-[var(--sidebar-bg)]',
        'transition-transform duration-200 ease-out',
        mobileOpen ? 'translate-x-0' : '-translate-x-full'
      )}>
        <div className="absolute top-0 left-0 right-0 h-px opacity-50"
          style={{ background: 'linear-gradient(90deg, transparent, #2d5eff, transparent)' }} />

        <div className="flex h-[56px] shrink-0 items-center justify-between border-b border-[var(--border)] px-4">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-[var(--bg-surface-2)] border border-[var(--border)]">
              <img src={LOGO_PROCREDITO} alt="ProCredito" className="h-6 w-6 object-contain" />
            </div>
            <div>
              <p className="text-[13px] font-bold text-[var(--text-primary)] leading-none tracking-tight">PROCREDITO</p>
              <p className="text-[9px] text-[var(--text-muted)] mt-0.5 tracking-[0.15em] uppercase">Dominicana</p>
            </div>
          </div>
          <button onClick={closeMenu}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--nav-hover-bg)] transition-colors">
            <X size={15} strokeWidth={1.8} />
          </button>
        </div>

        <NavContent collapsed={false} />

        {profile && (
          <div className="shrink-0 border-t border-[var(--border)] p-3">
            <div className="rounded-lg border border-[var(--border)] bg-[var(--bg-surface-2)]/50 p-2.5">
              <div className="flex items-center gap-2.5">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-500/15 text-brand-400 font-bold text-sm border border-brand-500/20">
                  {initial}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[11px] font-semibold text-[var(--text-primary)] leading-none">
                    {profile.full_name ?? profile.username}
                  </p>
                  <div className="mt-1"><RoleBadge role={profile.role} /></div>
                </div>
                <button onClick={async () => { closeMenu(); await handleSignOut() }}
                  className="shrink-0 p-1.5 rounded text-[var(--text-muted)] hover:text-red-400 hover:bg-red-400/10 transition-colors">
                  <LogOut size={13} strokeWidth={1.8} />
                </button>
              </div>
            </div>
          </div>
        )}
      </aside>
    </>
  )
}
